
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { Loader2 } from "lucide-react";

export default function FacebookCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log("Processing Facebook callback...");
        console.log("Search params:", Object.fromEntries(searchParams.entries()));

        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const error = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");

        if (error) {
          console.error("Facebook OAuth error:", error, errorDescription);
          toast.error(`Erro de autorização: ${errorDescription || error}`);
          navigate("/clients");
          return;
        }

        if (!code) {
          console.error("No authorization code received");
          toast.error("Código de autorização não recebido");
          navigate("/clients");
          return;
        }

        if (!state) {
          console.error("No state parameter received");
          toast.error("Parâmetro de estado não recebido");
          navigate("/clients");
          return;
        }

        let clientId;
        try {
          const stateData = JSON.parse(decodeURIComponent(state));
          clientId = stateData.clientId;
          console.log("Parsed state data:", stateData);
        } catch (parseError) {
          console.error("Error parsing state:", parseError);
          toast.error("Erro ao processar dados de estado");
          navigate("/clients");
          return;
        }

        if (!clientId) {
          console.error("No client ID in state");
          toast.error("ID do cliente não encontrado");
          navigate("/clients");
          return;
        }

        console.log("Processing OAuth for client:", clientId);

        // Exchange code for access token
        const tokenResponse = await fetch('https://graph.facebook.com/v18.0/oauth/access_token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            client_id: '579174651369726',
            client_secret: 'ef5d5b4bc6f5b6fcfce14d4a1ba51b32',
            redirect_uri: `${window.location.origin}/auth/facebook/callback`,
            code: code,
          }),
        });

        const tokenData = await tokenResponse.json();
        console.log("Token exchange response:", tokenData);

        if (!tokenData.access_token) {
          console.error("No access token received:", tokenData);
          toast.error("Erro ao obter token de acesso");
          navigate("/clients");
          return;
        }

        // Get user info
        const userResponse = await fetch(
          `https://graph.facebook.com/me?fields=id,name&access_token=${tokenData.access_token}`
        );
        const userData = await userResponse.json();
        console.log("User data:", userData);

        // Get Instagram business accounts
        const businessResponse = await fetch(
          `https://graph.facebook.com/me/accounts?fields=id,name,instagram_business_account&access_token=${tokenData.access_token}`
        );
        const businessData = await businessResponse.json();
        console.log("Business accounts:", businessData);

        let instagramAccount = null;
        let instagramData = null;

        if (businessData.data && businessData.data.length > 0) {
          for (const page of businessData.data) {
            if (page.instagram_business_account) {
              instagramAccount = page.instagram_business_account;
              
              // Get Instagram account details
              const igResponse = await fetch(
                `https://graph.facebook.com/${instagramAccount.id}?fields=id,username,followers_count,profile_picture_url&access_token=${tokenData.access_token}`
              );
              instagramData = await igResponse.json();
              console.log("Instagram data:", instagramData);
              break;
            }
          }
        }

        // Remove existing accounts for this client to avoid duplicates
        console.log("Removing existing accounts for client:", clientId);
        const { error: deleteError } = await supabase
          .from('social_accounts')
          .delete()
          .eq('client_id', clientId)
          .eq('platform', 'instagram');

        if (deleteError) {
          console.error("Error deleting existing accounts:", deleteError);
        }

        // Save to database
        const accountData = {
          client_id: clientId,
          platform: 'instagram',
          account_name: instagramData?.username || userData.name,
          account_url: instagramData?.username ? `https://instagram.com/${instagramData.username}` : null,
          followers_count: instagramData?.followers_count || 0,
          access_token: tokenData.access_token,
        };

        console.log("Saving account data:", accountData);

        const { data: savedAccount, error: saveError } = await supabase
          .from('social_accounts')
          .insert(accountData)
          .select()
          .single();

        if (saveError) {
          console.error("Error saving account:", saveError);
          toast.error("Erro ao salvar conta conectada");
          navigate("/clients");
          return;
        }

        console.log("Account saved successfully:", savedAccount);
        toast.success("Conta Instagram conectada com sucesso!");

        // Redirect to client detail page
        navigate(`/clients/${clientId}`);

      } catch (error) {
        console.error("Error in Facebook callback:", error);
        toast.error("Erro ao processar autenticação");
        navigate("/clients");
      } finally {
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-gray-600">
          {isProcessing ? "Processando autenticação..." : "Redirecionando..."}
        </p>
      </div>
    </div>
  );
}
